import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/travis/build/sgrishchenko/reselect-utils/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "path--prop-selectors"
    }}>{`Path & Prop Selectors`}</h1>
    <h2 {...{
      "id": "path-selectors"
    }}>{`Path Selectors`}</h2>
    <p><inlineCode parentName="p">{`Path Selector`}</inlineCode>{` helper is useful when you try to describe deep nested dependencies of your selector. Suppose you have such state:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const state = {
  person: {
    id: 1,
    firstName: 'Marry',
    secondName: 'Poppins',
  },
};
`}</code></pre>
    <p>{`Lets's write a simple selector for it:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const personFullNameSelector = createSelector(
  (state) => state.person.firstName,
  (state) => state.person.secondName,
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
);
`}</code></pre>
    <p>{`It is working example and now we don't need any helper to implement it. But what if our `}<inlineCode parentName="p">{`person`}</inlineCode>{` object is optional. We can re-write our example next way:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const personFullNameSelector = createSelector(
  (state) => state.person && state.person.firstName,
  (state) => state.person && state.person.secondName,
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
);
`}</code></pre>
    <p>{`I can also use `}<a parentName="p" {...{
        "href": "https://github.com/tc39/proposal-optional-chaining"
      }}>{`optional chaining`}</a>{` to reduce and simplify code:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const personFullNameSelector = createSelector(
  (state) => state.person?.firstName,
  (state) => state.person?.secondName,
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
);
`}</code></pre>
    <p>{`Yes, we still don't need any helper now. But what if we have normalized state with few optional persons:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const state = {
  persons: {
    1: {
      id: 1,
      firstName: 'Marry',
      secondName: 'Poppins',
    },
    2: {
      id: 2,
      firstName: 'Harry',
      secondName: 'Potter',
    },
  },
};
`}</code></pre>
    <p>{`In this case you need provide `}<inlineCode parentName="p">{`personId`}</inlineCode>{` property in your selector. `}<a parentName="p" {...{
        "href": "https://github.com/toomuchdesign/re-reselect"
      }}>{`Re-reselect`}</a>{` library is better for parametric selector, so now we can write something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { createCachedSelector } from 're-reselect';

const personFullNameSelector = createCachedSelector(
  (state, props) => state.persons[props.personId]?.firstName,
  (state, props) => state.persons[props.personId]?.secondName,
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
)({
  keySelector: (state, props) => props.personId,
});
`}</code></pre>
    <p>{`If you use `}<a parentName="p" {...{
        "href": "https://www.typescriptlang.org/"
      }}>{`TypeScript`}</a>{` for static typing this example can look more complex:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const personFullNameSelector = createCachedSelector(
  (state: State, props: Props) => state.persons[props.personId]?.firstName,
  (state: State, props: Props) => state.persons[props.personId]?.secondName,
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
)({
  keySelector: (state, props) => props.personId,
});
`}</code></pre>
    <p>{`Now we can use `}<inlineCode parentName="p">{`Path Selector`}</inlineCode>{` to reduce boilerplate code:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { createPathSelector } from 'reselect-utils';

const personsSelector = (state: State) => state.persons;

const personSelector = createCachedSelector(
  personsSelector,
  (state: State, props: Props) => props.personId,
  (persons, personId) => persons[personId],
)({
  keySelector: (state, props) => props.personId,
});

const personFullNameSelector = createCachedSelector(
  createPathSelector(personSelector).firstName(),
  createPathSelector(personSelector).secondName(),
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
)({
  keySelector: (state, props) => props.personId,
});
`}</code></pre>
    <h2 {...{
      "id": "prop-selector"
    }}>{`Prop Selector`}</h2>
    <p>{`There is another `}<inlineCode parentName="p">{`Prop Selector`}</inlineCode>{` helper for properties selection. `}<inlineCode parentName="p">{`Prop Selector`}</inlineCode>{` is built on top `}<inlineCode parentName="p">{`Path Selector`}</inlineCode>{`. Also, there are short aliases for `}<inlineCode parentName="p">{`Prop Selector`}</inlineCode>{` and `}<inlineCode parentName="p">{`Path Selector`}</inlineCode>{` helper. With these helpers you can re-write your code like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { path, prop } from 'reselect-utils';

const personsSelector = (state: State) => state.persons;

const personSelector = createCachedSelector(
  personsSelector,
  prop<Props>().personId(),
  (persons, personId) => persons[personId],
)({
  keySelector: prop<Props>().personId(),
});

const personFullNameSelector = createCachedSelector(
  path(personSelector).firstName(),
  path(personSelector).secondName(),
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
)({
  keySelector: prop<Props>().personId(),
});
`}</code></pre>
    <p>{`You can set up default values in `}<inlineCode parentName="p">{`Path Selector`}</inlineCode>{`, if you need:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const personFullNameSelector = createCachedSelector(
  path(personSelector).firstName('John'), // <- Default value is used here
  path(personSelector).secondName('Doe'), // <- and here
  (firstName, secondName) => \`\${firstName} \${secondName}\`,
)({
  keySelector: prop<Props>().personId(),
});
`}</code></pre>
    <p>{`Now we have fully typed selector, that is cached by different properties.`}</p>
    <p>{`You can also work with objects of unlimited nesting:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { path } from 'reselect-utils';

const personSelectorInfo = createSelector(
  path(personSelector).address.street('-'),
  path(personSelector).some.very.deep.field(123),
  (street, field) => ({ street, field }),
)({
  keySelector: prop<Props>().personProps.personId(),
});
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      